import { useCallback, useState } from 'react'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { type AccountOpeningPrefill } from '../../../models/AccountOpeningDescriptor'
import { fetchPrefilled, type PrefillResults, type PrefillTarget } from '../domain/prefillSources'

interface Prefill {
  run: (targets: PrefillTarget[]) => Promise<void>
  isRunning: (key: string) => boolean
  hasFailed: (key: string) => boolean
  isLoading: boolean
  error: string | null
}

export const usePrefill = ({
  accountId,
  prefill,
  errorMessage,
  onFilled,
}: {
  accountId: string
  prefill?: AccountOpeningPrefill
  errorMessage: string
  onFilled: (filled: PrefillResults) => void
}): Prefill => {
  const [running, setRunning] = useState<string[]>([])
  const [failed, setFailed] = useState<string[]>([])
  const [executePrefill] = useExecuteAiAgentMutation()

  const run = useCallback(
    async (targets: PrefillTarget[]) => {
      if (!prefill || targets.length === 0) {
        return
      }
      const keys = targets.map((target) => target.key)
      const notInThisRun = (key: string): boolean => !keys.includes(key)
      setFailed((previous) => previous.filter(notInThisRun))
      setRunning((previous) => [...previous.filter(notInThisRun), ...keys])

      const { filled, failedKeys } = await fetchPrefilled({ executePrefill, accountId, prefill, targets, suppressGlobalErrorToast: true })

      if (filled.size > 0) {
        onFilled(filled)
      }
      setRunning((previous) => previous.filter(notInThisRun))
      setFailed((previous) => [...previous.filter(notInThisRun), ...failedKeys])
    },
    [prefill, accountId, executePrefill, onFilled],
  )

  return {
    run,
    isRunning: (key) => running.includes(key),
    hasFailed: (key) => failed.includes(key),
    isLoading: running.length > 0,
    error: failed.length > 0 ? errorMessage : null,
  }
}
